import { computed, ref, watch } from 'vue'
import type { ComputedRef } from 'vue'
import { mediaSizeFor, requiresEntryCompletion } from '../utils/media-presentation'
import type { GuidedMediaSize } from '../utils/media-presentation'
import type { GuidedAnswer, GuidedModeMedia } from '../types/guided-mode.types'

export interface GuidedMediaItem {
  conceptId: string
  media: GuidedModeMedia | null
  answer: GuidedAnswer
  collectionType: string
  isBlocking: boolean
}

export interface GuidedMediaPresentation {
  size: GuidedMediaSize
  audioEnabled: boolean
}

interface UseGuidedMediaPresentationOptions {
  items: ComputedRef<GuidedMediaItem[]>
  isMultiQuestionPage: ComputedRef<boolean>
  screenKey: ComputedRef<string>
}

function hasAnswer(answer: GuidedAnswer): boolean {
  return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
}

export function useGuidedMediaPresentation(options: UseGuidedMediaPresentationOptions) {
  const { items, isMultiQuestionPage, screenKey } = options
  const sizeOverrides = ref<Record<string, GuidedMediaSize>>({})
  const committedValues = ref<Record<string, GuidedAnswer>>({})
  const previousAnswered = ref<Record<string, boolean>>({})
  const previousAnswerValues = ref<Record<string, GuidedAnswer>>({})
  const groupSizeOverride = ref<GuidedMediaSize | null>(null)
  const audibleId = ref<string | null>(null)

  function seedCommitted() {
    const next: Record<string, GuidedAnswer> = {}
    for (const item of items.value) {
      if (hasAnswer(item.answer)) next[item.conceptId] = item.answer
    }
    committedValues.value = next
  }

  function reset() {
    sizeOverrides.value = {}
    groupSizeOverride.value = null
    audibleId.value = null
    seedCommitted()
    previousAnswered.value = Object.fromEntries(
      items.value.map(item => [item.conceptId, hasAnswer(item.answer)]),
    )
    previousAnswerValues.value = Object.fromEntries(
      items.value.map(item => [item.conceptId, item.answer]),
    )
  }

  watch(screenKey, reset, { immediate: true, flush: 'sync' })

  watch(items, current => {
    const present = new Set(current.map(item => item.conceptId))
    const nextOverrides = { ...sizeOverrides.value }
    const nextCommitted = { ...committedValues.value }
    let overridesChanged = false
    let committedChanged = false
    for (const conceptId of Object.keys(nextOverrides)) {
      if (present.has(conceptId)) continue
      delete nextOverrides[conceptId]
      overridesChanged = true
    }
    for (const conceptId of Object.keys(nextCommitted)) {
      if (present.has(conceptId)) continue
      delete nextCommitted[conceptId]
      committedChanged = true
    }
    if (audibleId.value !== null && !present.has(audibleId.value)) audibleId.value = null
    for (const item of current) {
      const answered = hasAnswer(item.answer)
      if (previousAnswered.value[item.conceptId] && !answered && nextOverrides[item.conceptId]) {
        delete nextOverrides[item.conceptId]
        overridesChanged = true
      }
      if (item.conceptId in nextCommitted && nextCommitted[item.conceptId] !== item.answer) {
        delete nextCommitted[item.conceptId]
        committedChanged = true
      }
      if (item.conceptId === audibleId.value && previousAnswerValues.value[item.conceptId] !== item.answer) {
        audibleId.value = null
      }
    }
    previousAnswered.value = Object.fromEntries(current.map(item => [item.conceptId, hasAnswer(item.answer)]))
    previousAnswerValues.value = Object.fromEntries(current.map(item => [item.conceptId, item.answer]))
    if (overridesChanged) sizeOverrides.value = nextOverrides
    if (committedChanged) committedValues.value = nextCommitted
  }, { flush: 'sync' })

  function isEntryComplete(item: GuidedMediaItem): boolean {
    return !requiresEntryCompletion(item.collectionType)
      || committedValues.value[item.conceptId] === item.answer
  }

  const mediaState = computed<Record<string, GuidedMediaPresentation>>(() => {
    const state: Record<string, GuidedMediaPresentation> = {}
    for (const item of items.value) {
      state[item.conceptId] = {
        size: mediaSizeFor({
          hasMedia: Boolean(item.media),
          isMultiQuestionPage: isMultiQuestionPage.value,
          hasAnswer: hasAnswer(item.answer),
          requiresEntryCompletion: requiresEntryCompletion(item.collectionType),
          entryComplete: isEntryComplete(item),
          isBlocking: item.isBlocking,
          override: sizeOverrides.value[item.conceptId] ?? null,
        }),
        audioEnabled: audibleId.value === item.conceptId,
      }
    }
    return state
  })

  function toggleSize(conceptId: string) {
    const current = mediaState.value[conceptId]?.size ?? 'default'
    const next = current === 'default' ? 'thumbnail' : 'default'
    sizeOverrides.value = { ...sizeOverrides.value, [conceptId]: next }
    if (next === 'thumbnail' && audibleId.value === conceptId) audibleId.value = null
  }

  function requestAudible(conceptId: string) {
    audibleId.value = conceptId
  }

  function stopAudible() {
    audibleId.value = null
  }

  function markEntryComplete(conceptId: string) {
    const item = items.value.find(candidate => candidate.conceptId === conceptId)
    if (!item) return
    committedValues.value = { ...committedValues.value, [conceptId]: item.answer }
  }

  const groupAnswered = computed(() => isMultiQuestionPage.value
    && items.value.length > 0
    && items.value.every(item => hasAnswer(item.answer) && isEntryComplete(item) && !item.isBlocking))

  watch(groupAnswered, answered => {
    if (!answered) groupSizeOverride.value = null
  }, { flush: 'sync' })

  const groupMediaState = computed<GuidedMediaPresentation>(() => ({
    size: groupSizeOverride.value ?? (groupAnswered.value ? 'thumbnail' : 'default'),
    audioEnabled: false,
  }))

  function toggleGroupSize() {
    groupSizeOverride.value = groupMediaState.value.size === 'default' ? 'thumbnail' : 'default'
  }

  return {
    mediaState, toggleSize, markEntryComplete, requestAudible, stopAudible,
    groupMediaState, toggleGroupSize, reset,
  }
}
